import Axios, { AxiosResponse } from 'axios'
import { TFunction } from 'i18next'
import appConfig from 'app.config'

import { IMobileIdAuthResponse } from 'interfaces/i-auth/i-mobileid'
import { IUserData } from 'interfaces/i-auth/i-user-data'
import { SmartIdInitiateAuthDto } from 'interfaces/i-auth/i-smart-id'
import { ISignicatIdAuthResponse } from 'interfaces/i-auth/i-signicatid'
import { IBankIdAuthResponse } from 'interfaces/i-auth/i-bankid'
import {
	authenticationServiceCancelToken,
	getNorwegianBankIdReturnUrl,
	getReturnUrl,
	getSignicatReturnUrl,
} from '../utils/authHelpers'
import { authAndSignApiRoutes } from 'constants/routes/authAndSignApiRoutes'
import { cdaApiRoutes } from 'constants/routes/cdaApiRoutes'
import AuthMethodType from 'constants/values/authMethods'
import {
	IApplicationOtpStartRequest,
	ILoanOtpStartRequest,
	IOtpCompleteRequest,
	IOtpStartResponse,
} from 'interfaces/i-auth/i-otpid'
import axiosClients from './axiosClient'
import {
	IBankIdQrCodeResponse,
	IBankIdStartResponse,
} from 'interfaces/i-bankid'

const identityClient = axiosClients.getIdentityClient()
const cdaClient = axiosClients.getCdaClient()

export const cancelSource = Axios.CancelToken.source()

const authenticationService = {
	async smartIdAuthInit(authRequest: {
		countryCode: string
		nationalIdentityNumber: string
		returnUrl: string
	}) {
		const { data } = await identityClient.post<SmartIdInitiateAuthDto>(
			authAndSignApiRoutes.auth.smartIdAuthInitiate,
			authRequest,
		)

		return data
	},

	async smartIdAuthComplete(
		authRequest: SmartIdInitiateAuthDto,
		retryCount: number = 0,
	): Promise<{
		redirectUrl: string
		ok: boolean
		secret: string
		resultCode: string
	}> {
		try {
			console.log('retryC: ', retryCount)
			const { data } = await identityClient.post<{
				redirectUrl: string
				ok: boolean
				secret: string
				resultCode: string
			}>(authAndSignApiRoutes.auth.smartIdAuthComplete, authRequest)

			return data
		} catch (e) {
			// The request was made but no response was received
			// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
			if ((e as any).request) {
				if (retryCount < 3) {
					return await this.smartIdAuthComplete(
						authRequest,
						retryCount + 1,
					)
				}
			}

			throw e
		}
	},

	async mobileIdAuthInitiate(
		phoneNumber: string,
		personalNumber: string,
		returnUrl: string,
	) {
		phoneNumber = phoneNumber.replace(/\s+/g, '')
		const { data } = await identityClient.post<IMobileIdAuthResponse>(
			authAndSignApiRoutes.auth.mobileIdAuthInitiate,
			{
				phoneNumber,
				returnUrl,
				personalNumber,
			},
		)

		return data
	},

	async mobileIdAuthComplete(userData: IUserData | undefined, t: TFunction) {
		const { data } = await identityClient.post<{
			redirectUrl: string
			secret: string
		}>(authAndSignApiRoutes.auth.mobileIdAuthComplete, userData)

		if (!data) {
			throw new Error(t('AuthenticationError'))
		}

		return data
	},

	async cardIdAuthenticate(
		requestId: string,
		certificateContent: string,
		t: TFunction,
	) {
		const { data } = await identityClient.post(
			authAndSignApiRoutes.auth.idCardAuth(requestId),
			{ certificateContent },
		)

		return data as string
	},

	async signicatRedirectLinkGet() {
		const route =
			authAndSignApiRoutes.auth.signicatAuthLink +
			`?state=noState&locale=${
				appConfig.country
			}&callbackUrl=${getSignicatReturnUrl()}`

		const { data } = await identityClient.get<string>(route)
		return data
	},

	async signicatMitIdRedirectLinkGet() {
		const route =
			authAndSignApiRoutes.auth.signicatAuthMitId +
			`?state=noState&callbackUrl=${getSignicatReturnUrl()}`

		const { data } = await identityClient.get<string>(route)
		return data
	},

	async signicatResultGet(queryParams: string, t: TFunction) {
		const data = await identityClient.get<ISignicatIdAuthResponse>(
			authAndSignApiRoutes.auth.signicatAuthResult +
				`${queryParams}&callbackUrl=${getSignicatReturnUrl()}`,
		)

		if (data.status === 200) {
			return data.data
		}
		throw new Error(t('AuthenticationError'))
	},

	async norwegianBankIdRedirectLinkGet(authMethod: AuthMethodType) {
		let method = undefined
		switch (authMethod) {
			case AuthMethodType.norwegianBankId:
				method = 'nbid-high'
				break
			case AuthMethodType.norwegianBankIdBiometric:
				method = 'nbid-biometric'
				break
			default:
				throw new Error('Invalid authentication method')
		}

		const route =
			authAndSignApiRoutes.auth.norwegianBankIdAuthLink +
			`?state=noState&locale=${
				appConfig.country
			}&callbackUrl=${getNorwegianBankIdReturnUrl()}&method=${method}`
		const { data } = await identityClient.get<string>(route)
		return data
	},

	async norwegianBankIdApplicationResultGet(
		queryParams: string,
		t: TFunction,
	) {
		const data = await identityClient.get<ISignicatIdAuthResponse>(
			authAndSignApiRoutes.auth.norwegianBankIdApplicationAuthResult +
				`${queryParams}&callbackUrl=${getNorwegianBankIdReturnUrl()}`,
		)
		if (data.status === 200) {
			return data.data
		}
		throw new Error(t('AuthenticationError'))
	},

	async bankIdAuthInitiate(ssn: string, returnUrl: string) {
		const { data } = await identityClient.post<IBankIdAuthResponse>(
			authAndSignApiRoutes.auth.bankIdAuthInitiate,
			{
				returnUrl,
				ssn,
			},
		)

		return data
	},

	async bankIdAuthCancel(orderRef: string) {
		try {
			const response = await identityClient.post(
				authAndSignApiRoutes.auth.bankIdAuthCancel(orderRef),
			)
			return response.status
		} catch (error) {
			throw error
		}
	},

	async bankIdAuthComplete(
		orderRef: string,
		ssn: string,
		redirectUrl: string,
		t: TFunction,
	) {
		try {
			const { data } = await identityClient.post<{
				redirectUrl: string
				secret: string
				isCompleted: boolean
			}>(
				authAndSignApiRoutes.auth.bankIdAuthComplete,
				{
					redirectUrl,
					orderRef,
					ssn,
				},
				{
					cancelToken: cancelSource.token,
				},
			)

			if (!data) {
				throw new Error(t('AuthenticationError'))
			}

			return data
		} catch (error) {
			if (Axios.isCancel(error)) {
				cancelSource.cancel('Request canceled by user')
			}
		}
	},

	async bankIdTestAuth(ssn: string) {
		const config = {
			cancelToken: authenticationServiceCancelToken.getToken(),
		}
		const resp = await identityClient.get<{
			ok: boolean
			secret: string
		}>(authAndSignApiRoutes.auth.bankIdTestAuth(ssn), config)

		return resp.data
	},

	async getArtifact(
		requestId: string,
		taskId: string,
		isCreditCard: boolean,
		t: TFunction,
	) {
		let route
		switch (appConfig.country) {
			case 'dk':
				route = authAndSignApiRoutes.auth.getArtifactDk(
					requestId,
					taskId,
				)
				break
			case 'fi':
				route = authAndSignApiRoutes.auth.getArtifactFi(
					requestId,
					taskId,
				)
				break
			case 'no':
				route = authAndSignApiRoutes.auth.getArtifactBbBank(
					requestId,
					taskId,
					isCreditCard ? 'Avarda' : 'TFNordic',
				)
				break
			case 'se':
				route = authAndSignApiRoutes.auth.getArtifactSe(
					requestId,
					taskId,
				)
				break
			default:
				route = authAndSignApiRoutes.auth.getArtifactFi(
					requestId,
					taskId,
				)
		}
		console.log(route)
		const artifact = await identityClient.get<string>(route)

		if (artifact.status === 200) {
			return artifact.data
		}
		throw new Error(t('NoArtifact'))
	},

	async authenticate(
		apiRoute: string,
		ssn: string,
		birthDate: string,
		applicationId: string,
		t: TFunction,
	) {
		const resp = await identityClient.post<{
			secret: string
			returnUrl: string
		}>(apiRoute, {
			ssn,
			birthDate,
			applicationId,
			returnUrl: getReturnUrl(),
		})

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}
		console.log(resp.data)
		return resp.data
	},

	async ukAuthenticate(applicationId: string, token: string, t: TFunction) {
		const resp = await identityClient.post<{
			secret: string
			returnUrl: string
		}>(authAndSignApiRoutes.auth.ukAuthenticate, {
			applicationId,
			token,
			returnUrl: getReturnUrl(),
		})

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.data
	},

	async otpStart(request: IApplicationOtpStartRequest, t: TFunction) {
		const route = () => {
			switch (appConfig.country) {
				case 'es':
					return authAndSignApiRoutes.auth.otpStart
				default:
					return authAndSignApiRoutes.auth.otpStart
			}
		}

		const resp = await identityClient.post<{
			secret: string
			customerId: string
		}>(route(), request)

		return resp.data
	},

	async otpLoanStart(request: ILoanOtpStartRequest, t: TFunction) {
		const resp = await identityClient.post<
			ILoanOtpStartRequest,
			AxiosResponse<IOtpStartResponse>
		>(authAndSignApiRoutes.auth.loanOtpStart, request)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.data
	},

	async otpComplete(request: IOtpCompleteRequest, t: TFunction) {
		const route = () => {
			switch (appConfig.country) {
				case 'es':
					return authAndSignApiRoutes.auth.otpComplete
				default:
					return authAndSignApiRoutes.auth.otpComplete
			}
		}

		const resp = await identityClient.post<{
			redirectUrl: string
			secret: string
		}>(route(), request)

		return resp.data
	},

	async electronicIDAuthorizationToken(
		applicationId: string,
		product: string,
	) {
		const { data } = await identityClient.get<{
			id: string
			authorization: string
		}>(authAndSignApiRoutes.auth.electronicIDToken(applicationId, product))
		console.log('received auth: ', data)
		return data
	},

	async electronicIDVerificationRequest(videoId: string, product: string) {
		const { data } = await cdaClient.post(
			cdaApiRoutes.electronicIDVerificationRequest,
			{ videoId: videoId, product: product },
		)
		return data
	},

	redirectTo(url: string, secretValue: string) {
		const form = document.createElement('form')
		form.style.display = 'none'
		form.action = authAndSignApiRoutes.auth.redirectTo
		form.method = 'POST'

		var secret = document.createElement('input')
		secret.name = 'secret'
		secret.value = secretValue

		var redirectUrl = document.createElement('input')
		redirectUrl.name = 'redirectUrl'
		redirectUrl.value = url

		form.appendChild(secret)
		form.appendChild(redirectUrl)

		document.body.appendChild(form)
		form.submit()
	},

	async bankIdv2AuthInitiate(redirectUrl: string) {
		const response = await identityClient.get<IBankIdStartResponse>(
			authAndSignApiRoutes.auth.bankIdv2AuthInitiate +
				`?returnUrl=${redirectUrl}`,
		)
		return response.data
	},

	async bankIdv2AuthQrInitiate() {
		const response = await identityClient.get<IBankIdQrCodeResponse>(
			authAndSignApiRoutes.auth.bankIdv2AuthQrInitiate,
		)
		return response.data
	},

	async bankIdv2QrCodeContinue(sessionId: string, t: TFunction) {
		const resp = await identityClient.post<IBankIdQrCodeResponse>(
			authAndSignApiRoutes.auth.bankIdv2QrCodeContinue,
			{
				sessionId,
			},
		)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.data
	},

	async bankIdv2AuthCancel(orderRef: string, t: TFunction) {
		const resp = await identityClient.post<any>(
			authAndSignApiRoutes.auth.bankIdv2AuthCancel,
			{
				orderRef,
			},
		)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.status
	},

	async bankIdv2AuthCollect(
		orderRef: string,
		isAutoStart: boolean,
		t: TFunction,
	) {
		const config = {
			cancelToken: authenticationServiceCancelToken.getToken(),
		}
		const resp = await identityClient.post<{
			status: string
			isCompleted: boolean
			secret: string
			ssn: string
		}>(
			authAndSignApiRoutes.auth.bankIdv2AuthCollect,
			{
				orderRef,
				isAutoStart,
			},
			config,
		)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.data
	},
}

export default authenticationService
